/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Master Settings Page of this app to add country, course, language and documents.
 */
import React, { useState } from "react";
import { Box, Stack, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import { CountrySelection } from "./CountrySelection";
import { CourseSelection } from "./CourseSelection";
import { DetailsPanel } from "./DetailsPanel";
import { useFetchData } from "../../components/hooks/useFetchData";
import { FooterSection } from "./FooterSection";
import { LoadingBackdrop } from "../../components/common/LoadingBackdrop";

const MasterSettingsPage = () => {
  const [value, setValue] = useState(0);
  const [countryList, setCountryList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [documentList, setDocumentList] = useState([]);
  const [newCountry, setNewCountry] = React.useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [selectedCourse, setSelectedCourse] = useState({});
  const [minLoading, setMinLoading] = useState(true);
  const [newCourse, setNewCourse] = useState([]);
  const [newLanguages, setNewLanguages] = useState([]);
  const [newDocuments, setNewDocuments] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [lifecycleEvents, setLifecycleevents] = useState([]);
  const [categories, setCategories] = useState([]);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { fetchData, loading } = useFetchData();

  const getAllCountries = async () => {
    const result = await fetchData("getcountries?showActive=0");
    if (result?.code === 200) {
      setCountryList(result.data);
      const activeCountries = result.data.filter((item) => item.isactive === 1);
      if (activeCountries?.length > 0) {
        setSelectedCountry(activeCountries[0]?.id);
      }
    }
  };

  const getAllCourses = async () => {
    const result = await fetchData("getcourses?showActive=1");
    if (result?.code === 200) {
      setCourseList(result.data);
      // if (result.data.length > 0) {
      //   setSelectedCourse(result.data[0]);
      // }
    }
  };

  const getAllLanguages = async () => {
    const result = await fetchData("getlanguages?showActive=1");
    if (result?.code === 200) {
      setLanguageList(result.data);
    }
  };

  const getAllDocuments = async () => {
    const result = await fetchData("getdocuments?showActive=1");
    if (result?.code === 200) {
      setDocumentList(result.data);
    }
  };

  const getCoursesByCountry = async () => {
    if (!selectedCountry) return;
    const body = { countryid: selectedCountry };
    const result = await fetchData("getcoursesbycountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setNewCourse(result.data.map((course) => course.courseid));
    }
  };

  const getLanguagesByCountry = async () => {
    if (!selectedCountry) return;
    const body = { countryid: selectedCountry };
    const result = await fetchData("getlanguagesbycountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setNewLanguages(result.data.map((language) => language.languageid));
    }
  };

  const getLifecycleEvents = async () => {
    const result = await fetchData("getlifecycleevents");
    if (result?.code === 200) {
      setLifecycleevents(result.data.LifecycleEvents);
    }
  };

  const getInstallments = async () => {
    if (!selectedCourse?.id || !selectedCountry) return;
    const body = { courseid: selectedCourse.id, countryid: selectedCountry };
    const result = await fetchData("getinstallments", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setInstallments(result.data);
    }
  };

  const getDocumentsByCourses = async () => {
    if (!selectedCourse?.id || !selectedCountry) return;
    const body = { courseid: selectedCourse.id, countryid: selectedCountry };
    const result = await fetchData("getdocumentsbycourses", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      const apiDocumentSets = result.data;

      setNewDocuments((prev) => {
        return apiDocumentSets.reduce((updatedDocuments, documentSet) => {
          const {
            courseid,
            countryid,
            documentset,
            documents: apiDocuments,
          } = documentSet;

          const existingDocumentSet = updatedDocuments.find(
            (c) =>
              c.courseid === courseid &&
              c.countryid === countryid &&
              c.documentset === documentset
          );

          if (existingDocumentSet) {
            const mergedDocuments = Array.from(
              new Set([...existingDocumentSet.documents, ...apiDocuments])
            );

            return updatedDocuments.map((c) =>
              c.courseid === courseid &&
              c.countryid === countryid &&
              c.documentset === documentset
                ? { ...c, documents: mergedDocuments }
                : c
            );
          } else {
            return [
              ...updatedDocuments,
              {
                courseid,
                countryid,
                documentset,
                documents: apiDocuments,
              },
            ];
          }
        }, prev);
      });
    }
  };

  const getCategories = async () => {
    const result = await fetchData("getcategories");
    if (result?.code === 200) {
      setCategories(result.data);
    }
  };

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getAllCountries();
      await getAllCourses();
      await getAllLanguages();
      await getAllDocuments();
      await getCoursesByCountry();
      await getLanguagesByCountry();
      await getDocumentsByCourses();
      await getLifecycleEvents();
      await getInstallments();
      await getCategories();
    };

    fetchDataAsync();

    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setNewCourse([]);
    setNewLanguages([]);
    const fetchDataAsync = async () => {
      await getCoursesByCountry();
      await getLanguagesByCountry();
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getDocumentsByCourses();
      await getInstallments();
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourse]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleUpdateCountryStatus = async () => {
    const body = {
      countries: newCountry,
    };
    const result = await fetchData("updatecountrystatus", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      getAllCountries();
    }
  };

  const handleManageCourseCountry = async () => {
    const body = {
      coursecountry: [
        {
          countryid: selectedCountry,
          courses: newCourse,
        },
      ],
    };
    const result = await fetchData("managecoursecountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      getCoursesByCountry();
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const handleManageLanguageCountry = async () => {
    const body = {
      languagecountry: [
        {
          countryid: selectedCountry,
          languages: newLanguages,
        },
      ],
    };
    const result = await fetchData("managelanguagecountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      getLanguagesByCountry();
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  // remove empty installments from array
  const removeEmptyInstallments = (installments) => {
    return installments.filter(
      (installment) =>
        installment.installmentamount?.trim() !== "" || installment.lifecycleid
    );
  };

  const handleManageInstallments = async () => {
    const instArray = await removeEmptyInstallments(installments);
    const body = {
      Installmentlifecycle: instArray,
    };
    const result = await fetchData("saveinstallment", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      getLanguagesByCountry();
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const handleManageDocumentCourse = async () => {
    if (newDocuments.length === 0) return;
    const body = {
      documentcourse: newDocuments,
    };
    const result = await fetchData("managedocumentcourse", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      getLanguagesByCountry();
    } else {
      enqueueSnackbar(result?.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const handleSave = async () => {
    await handleManageCourseCountry();
    await handleManageLanguageCountry();
    await handleManageDocumentCourse();
    await handleManageInstallments();
  };

  const handleManageCourse = async () => {
    navigate(`/master-settings/courses`);
  };

  const handleViewDetails = async () => {
    if (value === 0) {
      navigate(`/master-settings/languages`);
    } else {
      navigate(`/master-settings/documents`);
    }
  };

  const isLoading =
    minLoading ||
    loading["getcountries?showActive=0"] ||
    loading["getcourses?showActive=1"] ||
    loading["getlanguages?showActive=1"] ||
    loading["getdocuments?showActive=1"];

  const saveLoading =
    loading["managecoursecountry"] ||
    loading["managelanguagecountry"] ||
    loading["managedocumentcourse"];

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      overflow={{
        xs: "auto",
        md: "hidden",
      }}
    >
      <CountrySelection
        newCountry={newCountry}
        setNewCountry={setNewCountry}
        countryList={countryList}
        activeCountries={countryList.filter((item) => item.isactive === 1)}
        isLoading={isLoading}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        setSelectedCourse={setSelectedCourse}
        handleUpdateCountry={handleUpdateCountryStatus}
      />
      <Divider />
      <Stack
        pt={1}
        flex={1}
        overflow={{ xs: "unset", md: "hidden" }}
        direction={{ xs: "column", md: "row" }}
        columnGap={1}
        rowGap={2}
      >
        <CourseSelection
          isLoading={isLoading}
          selectedCountry={selectedCountry}
          setNewDocument={setNewDocuments}
          selectedCourse={selectedCourse}
          setSelectedCourse={setSelectedCourse}
          courses={courseList}
          newCourse={newCourse}
          setNewCourse={setNewCourse}
          handleAddNewCourse={handleManageCourse}
          categories={categories}
        />
        <DetailsPanel
          isLoading={isLoading}
          value={value}
          handleChange={handleChange}
          selectedCountry={selectedCountry}
          selectedCourse={selectedCourse}
          handleViewDetails={handleViewDetails}
          languageList={languageList}
          newLanguage={newLanguages}
          setNewLanguages={setNewLanguages}
          documentList={documentList}
          newDocuments={newDocuments}
          setNewDocuments={setNewDocuments}
          installments={installments}
          setInstallments={setInstallments}
          lifecycleEvents={lifecycleEvents}
        />
      </Stack>
      <FooterSection isLoading={isLoading} handleSave={handleSave} />
      <LoadingBackdrop isLoading={saveLoading} />
    </Box>
  );
};

export default MasterSettingsPage;
